import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useBulkUpsertUomConversions } from 'companySettings/hooks';
import { IUomConversionAuditRow } from 'companySettings/types';
import { FormProvider, useNotificationContext } from 'core/states';
import { IUom } from 'parts/types';

import { BulkUpsertUomConversionsFormFields } from './BulkUpsertUomConversionsFormFields';
import { getConversionPartIds, getSelectionUomPair, mapConversionsPayload } from './mappers';
import { bulkUpsertUomConversionsSchema } from './schema';
import { IBulkUpsertUomConversionsForm } from './types';

interface IProps {
  selectedRows: IUomConversionAuditRow[];
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Adds one part-level UOM conversion to every selected part, through the entity
 * endpoint the single-part form already uses and with the same three fields.
 */
export const BulkUpsertUomConversions = ({ selectedRows, isOpen, onClose }: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { bulkUpsertUomConversions } = useBulkUpsertUomConversions();

  const partCount = useMemo(() => getConversionPartIds(selectedRows).length, [selectedRows]);

  // Seeded from the rows so the pair is the one that clears them. The base is
  // passed by name only: the picker matches options on `name`, and the payload
  // reads nothing else off it.
  const defaultValues = useMemo<IBulkUpsertUomConversionsForm>(() => {
    const { baseUom, extractedUom } = getSelectionUomPair(selectedRows);

    return {
      baseUom: baseUom ? ({ name: baseUom } as IUom) : undefined,
      alternateUom: extractedUom ?? '',
      multiplier: undefined,
    };
  }, [selectedRows]);

  const handleOnClose = (reset?: UseFormReset<IBulkUpsertUomConversionsForm>) => {
    clearNotification();
    onClose();
    reset?.(defaultValues);
  };

  const onSubmit =
    (reset?: UseFormReset<IBulkUpsertUomConversionsForm>) =>
    async (data: IBulkUpsertUomConversionsForm) => {
      const conversions = mapConversionsPayload(selectedRows, data);

      if (0 === conversions.length) {
        return;
      }

      const result = await bulkUpsertUomConversions(conversions);

      if (!result?.success) {
        return;
      }

      setTimeout(() => handleOnClose(reset), 1000);
    };

  return (
    <FormProvider
      schema={bulkUpsertUomConversionsSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <BulkUpsertUomConversionsFormFields
        partCount={partCount}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
